/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { EntityVersion, ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  identity,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { Observable } from 'rxjs';
import {
  JeAuthorizeResponseDto,
  JeCcCompleteDto,
  JeCcOrderDetailsDto,
  JeDetailsDto,
  JeDirtyDataDto,
  JeGenerateItemIdsResponseDto,
  JeGetComponentsResponseDto,
  JeOrderGeneratedDto,
} from './job-estimate.dto';
import {
  EstimateOrdersMapper,
  JeAuthorizeResponseMapper,
  JeCcOrderDetailsMapper,
  JeDetailsDirtyDataMapper,
  JeDetailsMapper,
  JeGenerateItemIdsResponseMapper,
  JeGetCcCompleteMapper,
  JeGetComponentsResponseMapper,
  JeOrderGeneratedMapper,
} from './job-estimate.mapper';
import { JobListFilterDto, JobListItemDto } from '../job.dto';
import { JobListFilterMapper, JobListItemMapper } from '../job.mapper';
import { JeOrderForSchedule } from '../../../builder-job-admin/jobs/resources/job-estimate-details.model';
import { ComponentRequest } from 'builder-shared-data/components';

@Injectable()
export class JobEstimateRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  get(jobId: ID): Observable<JeDetailsDto> {
    return this.httpGet(
      `${this.bp}/jobs/${jobId}/estimate`,
      optimisticLockingOf(JeDetailsMapper));
  }

  recalculate(jobId: ID, dirtyData: JeDirtyDataDto): Observable<JeDetailsDto> {
    return this.httpPost(
      `${this.bp}/jobs/${jobId}/estimate/recalculate`,
      dirtyData,
      JeDetailsDirtyDataMapper,
      optimisticLockingOf(JeDetailsMapper));
  }

  authorize(jobId: ID, version: EntityVersion): Observable<JeAuthorizeResponseDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/estimate/authorize/${version}`, JeAuthorizeResponseMapper);
  }

  unauthorize(jobId: ID, version: EntityVersion): Observable<JeAuthorizeResponseDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/estimate/unauthorized/${version}`, JeAuthorizeResponseMapper);
  }

  getCostCenterOrderDetails(jobId: ID,
                            costCenterId: ID,
                            dirtyData: JeDirtyDataDto): Observable<JeCcOrderDetailsDto> {
    return this.httpPost(
      `${this.bp}/jobs/${jobId}/estimate/costCenter/${costCenterId}/orderDetails`,
      dirtyData,
      JeDetailsDirtyDataMapper,
      JeCcOrderDetailsMapper);
  }

  getCostCenter(jobId: ID,
                costCenterId: ID,
                dirtyData: JeDirtyDataDto): Observable<JeCcCompleteDto> {
    return this.httpPost(
      `${this.bp}/jobs/${jobId}/estimate/costCenter/${costCenterId}`,
      dirtyData,
      JeDetailsDirtyDataMapper,
      JeGetCcCompleteMapper);
  }

  getComponents(jobId: ID, components: ComponentRequest[]): Observable<JeGetComponentsResponseDto> {
    return this.httpPost(
      `${this.bp}/jobs/${jobId}/estimate/components`,
      { components },
      identity(),
      JeGetComponentsResponseMapper);
  }

  generateItemIds(count: number): Observable<JeGenerateItemIdsResponseDto> {
    return this.httpGet(
      `${this.bp}/jobs/estimate/generate-item-ids/${count}`,
      JeGenerateItemIdsResponseMapper);
  }

  generateOrders(costCenterIds: ID[]): Observable<JeOrderGeneratedDto[]> {
    return this.httpPut(
      `${this.bp}/jobs/estimate/add-job-orders`,
      { costCenterIds },
      identity(),
      arrayOf(JeOrderGeneratedMapper));
  }

  findAllAsDataSource(filter: JobListFilterDto): SlicedDataSource<JobListItemDto> {
    return this.createSlicedDataSourceGet(`${this.jad}/job-estimates`,
                                          filter, JobListFilterMapper, arrayOf(JobListItemMapper));
  }

  estimateOrders(jobId: ID, jobOrders: JeOrderForSchedule[]): Observable<any> {
    return this.httpPost(
      `${this.bp}/jobs/${jobId}/estimate/orders`,
      {jobOrders},
      EstimateOrdersMapper,
      identity(),
      );
  }
}
